import { Authenticator } from '@aws-amplify/ui-react'
import CircularProgress from '@mui/material/CircularProgress'
import { ThemeProvider } from '@mui/material/styles'
import { Auth, Amplify } from 'aws-amplify'
import React, { Suspense, lazy } from 'react'
import { createRoot } from 'react-dom/client'
import { createBrowserRouter, RouterProvider } from 'react-router-dom'

import './index.css'
import 'config/dayjs'
import App from './App'
import awsconfig from './aws-exports'
import theme from './constants/theme'
import PrivateRoute from './pages/PrivateRoute'
import reportWebVitals from './reportWebVitals'

const FormEventRegistration = lazy(
  () => import('./components/FormEventRegistration'),
)
const ParticipantMoreInfo = lazy(
  () => import('./components/ParticipantMoreInfo'),
)
const PaymongoResult = lazy(() => import('./components/PaymongoResult'))
const SubmitJersey = lazy(() => import('./components/SubmitJersey'))
const UploadResults = lazy(() => import('./components/UploadResults'))
const AboutUs = lazy(() => import('./pages/AboutUs'))
const DownloadParticipants = lazy(
  () => import('./pages/admin/DownloadParticipants'),
)
const DownloadRefund = lazy(() => import('./pages/admin/DownloadRefund'))
const ManageParticipants = lazy(() => import('./pages/admin/Participants'))
const EventDetails = lazy(() => import('./pages/EventDetails'))
const Events = lazy(() => import('./pages/Events'))
const FormRefund = lazy(() => import('./pages/FormRefund'))
const Home = lazy(() => import('./pages/Home'))
const Login = lazy(() => import('./pages/Login'))
const OnsiteRegistration = lazy(() => import('./pages/OnsiteRegistration'))
const PastEvents = lazy(() => import('./pages/PastEvents'))
const ShoppingCartStepper = lazy(() => import('./pages/ShoppingCartStepper'))
const Waiver = lazy(() => import('./pages/Waiver'))

const SuspenseFallback = (
  <div
    style={{
      position: 'fixed',
      top: 0,
      left: 0,
      width: '100vw',
      height: '100vh',
      display: 'flex',
      alignItems: 'center',
      justifyContent: 'center',
      backgroundColor: 'rgba(255,255,255,0.3)',
      zIndex: 10000,
    }}>
    <CircularProgress />
  </div>
)

Auth.configure(awsconfig)
Amplify.configure(awsconfig)

// const isMaintenance = false

const router = createBrowserRouter([
  {
    path: '/',
    element: <App />,
    children: [
      {
        index: true,
        element: (
          <Suspense fallback={SuspenseFallback}>
            <Home />
          </Suspense>
        ),
      },
      {
        path: 'aboutus',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <AboutUs />
          </Suspense>
        ),
      },
      {
        path: 'waiver',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <Waiver />
          </Suspense>
        ),
      },
      {
        path: 'waiver/:slug',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <Waiver />
          </Suspense>
        ),
      },
      {
        path: 'login',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <Login />
          </Suspense>
        ),
      },
      {
        path: 'events',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <Events />
          </Suspense>
        ),
      },
      {
        path: 'pastEvents',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <PastEvents />
          </Suspense>
        ),
      },
      {
        path: 'events/:slug',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <EventDetails />
          </Suspense>
        ),
      },
      {
        path: 'formEvents/:slug',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <FormEventRegistration />
          </Suspense>
        ),
      },
      {
        path: 'jersey/:participantId',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <SubmitJersey />
          </Suspense>
        ),
      },
      {
        path: 'paymentResults',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <PaymongoResult />
          </Suspense>
        ),
      },
      {
        path: 'moreInfo/:participantId',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <ParticipantMoreInfo />
          </Suspense>
        ),
      },
      {
        path: 'register/:slug',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <ShoppingCartStepper />
          </Suspense>
        ),
      },
      {
        path: 'register/:slug/:categoryId',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <ShoppingCartStepper />
          </Suspense>
        ),
      },
      {
        path: 'uploadResults/:participantId',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <UploadResults />
          </Suspense>
        ),
      },
      {
        path: 'refund/:slug',
        element: (
          <Suspense fallback={SuspenseFallback}>
            <FormRefund />
          </Suspense>
        ),
      },
      {
        path: 'admin',
        element: <PrivateRoute />,
        children: [
          {
            path: 'downloadParticipants',
            element: (
              <Suspense fallback={SuspenseFallback}>
                <DownloadParticipants />
              </Suspense>
            ),
          },
          {
            path: 'participants',
            element: (
              <Suspense fallback={SuspenseFallback}>
                <ManageParticipants />
              </Suspense>
            ),
          },
          {
            path: 'downloadRefund',
            element: (
              <Suspense fallback={SuspenseFallback}>
                <DownloadRefund />
              </Suspense>
            ),
          },
        ],
      },
      {
        path: 'admin',
        element: <PrivateRoute />,
        children: [
          {
            path: 'onsiteRegistration',
            element: (
              <Suspense fallback={SuspenseFallback}>
                <OnsiteRegistration />
              </Suspense>
            ),
          },
          {
            path: 'onsiteRegistration/:slug',
            element: (
              <Suspense fallback={SuspenseFallback}>
                <OnsiteRegistration />
              </Suspense>
            ),
          },
        ],
      },
    ],
  },
])

const root = createRoot(document.getElementById('root') as HTMLElement)
root.render(
  <React.StrictMode>
    <ThemeProvider theme={theme}>
      <Authenticator.Provider>
        <RouterProvider router={router} />
      </Authenticator.Provider>
    </ThemeProvider>
  </React.StrictMode>,
)

// If you want to start measuring performance in your app, pass a function
// to log results (for example: reportWebVitals(console.log))
// or send to an analytics endpoint. Learn more: https://bit.ly/CRA-vitals
reportWebVitals()
